import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { blake3 } from "@noble/hashes/blake3";
import { utf8ToBytes } from "@noble/hashes/utils";
import { randomInt, createHash } from "crypto";
import { ReconnectCadence } from "@/types";
import { z } from "zod";
import { FilterRule } from "@/hooks/use-table-options";
import { getBaseUrl } from "@/lib/utils/url";

export const defaultPageSize = 25;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const normalizeWebsite = (w?: string | null) => {
  if (!w) return "";
  let s = w.trim().toLowerCase();
  if (s.startsWith("http://")) s = s.slice(7);
  if (s.startsWith("https://")) s = s.slice(8);
  if (s.startsWith("www.")) s = s.slice(4);
  if (s.endsWith("/")) s = s.slice(0, -1);
  return s;
};

export function getInitials(name: string, characterCount: number = 2) {
  return name
    .split(" ")
    .map(n => n[0])
    .join("")
    .slice(0, characterCount);
}

export const formatPrice = (price: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(price);
};

export const toSnakeCase = (str: string) =>
  str
    .replace(/([A-Z])/g, "_$1") // insert _ before capital letters
    .replace(/[-\s]/g, "_") // replace hyphens/spaces with underscores
    .toLowerCase();

export function toSnakeCaseObject<T extends object>(obj: T): Record<string, unknown> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      const snakeKey = toSnakeCase(key);
      acc[snakeKey] = value;
      return acc;
    },
    {} as Record<string, unknown>
  );
}

export const prettifyCamelCase = (str: string) => {
  return str
    .split(/(?=[A-Z])/)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
};

export const getIndefiniteArticle = (word: string): string => {
  if (!word) return "a";

  const exceptions = ["url"];

  for (const exc of exceptions) {
    if (word.startsWith(exc)) {
      return "a";
    }
  }

  const firstLetter = word.trim().charAt(0).toLowerCase();
  const vowels = ["a", "e", "i", "o", "u"];
  return vowels.includes(firstLetter) ? "an" : "a";
};

export const hashString = (input: string): string => {
  const hash = blake3(utf8ToBytes(input)); // Uint8Array
  return Buffer.from(hash).toString("hex");
};

// normalized url is the domain name without the protocol, e.g. "linkedin.com/in/john-doe"
export const getLinkedInUrlFromNormalized = (normalizedUrl: string) => {
  return `https://www.${normalizedUrl}`;
};

export const normalizeString = (string?: string | null) => {
  return (string ?? "").toLowerCase().trim();
};

// @deprecated use normalizeWebsite instead - this is only to be used for LinkedIn URLs
export const normalizeUrl = (url: string) => {
  if (!url) return "";
  return url
    .replace(/^https?:\/\//, "") // remove protocol
    .replace(/^www\./, "") // remove www.
    .replace(/^[a-z]{2}\.linkedin\.com/, "linkedin.com") // remove domain prefixes (ca., tr., etc.)
    .replace(/\?[^#]*/, "") // remove query parameters
    .replace(/\/+$/, ""); // remove trailing slashes
};

export const getValidUrl = (url: string | null | undefined): string | null => {
  if (!url) {
    return null;
  }

  try {
    if (/^(https?:\/\/)/.test(url)) {
      new URL(url);
      return url;
    }
    const fullUrl = `https://${url}`;
    new URL(fullUrl);
    return fullUrl;
  } catch {
    return null;
  }
};

export const normalizeAndHashUrl = (url: string) => {
  const normalizedUrl = normalizeUrl(url);

  // blake 3 hash
  const hashedUrl = hashString(normalizedUrl);

  return hashedUrl;
};

export const findOriginalUrl = (hashedUrl: string, urlList: string[]) => {
  const originalUrl = urlList.find(url => normalizeAndHashUrl(url) === hashedUrl);
  if (!originalUrl) {
    console.error(`Could not find original URL for hashed URL: ${hashedUrl}`);
  }
  return originalUrl;
};

export type LoopsVerificationCode = {
  pin_1: string;
  pin_2: string;
  pin_3: string;
  pin_4: string;
  pin_5: string;
  pin_6: string;
};

export const formatVerificationCode = (code: string): LoopsVerificationCode => {
  const codeArray = code.split("");
  if (codeArray.length !== 6) {
    throw new Error("Invalid verification code");
  }
  return {
    pin_1: codeArray[0],
    pin_2: codeArray[1],
    pin_3: codeArray[2],
    pin_4: codeArray[3],
    pin_5: codeArray[4],
    pin_6: codeArray[5],
  };
};

export const generateOtp = () => {
  return randomInt(100000, 999999).toString();
};

export const hashOtp = (otp: string) => {
  return createHash("sha256").update(`${otp}${process.env.NEXTAUTH_SECRET}`).digest("hex");
};

export const getOTPExpiration = () => {
  return new Date(Date.now() + 1000 * 60 * 10); // 10 minutes
};

export const buildQuery = (
  params: Record<string, string | number | boolean | undefined | object>
): string => {
  const qs = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;

    if (key === "filters" && Array.isArray(value)) {
      for (const rule of value as FilterRule[]) {
        if (!rule?.field || !rule?.operator) continue;

        const fieldPart =
          rule.field === "location" && rule.columns?.length
            ? `location:${rule.columns.join(",")}`
            : rule.field;

        const opPart = Array.isArray(rule.operator) ? rule.operator.join(",") : rule.operator;

        let rawVal: string;
        if (typeof rule.value === "object" && rule.value !== null && !Array.isArray(rule.value)) {
          rawVal = JSON.stringify(rule.value); // objects (e.g., LocationFilterOption)
        } else if (Array.isArray(rule.value)) {
          rawVal = rule.value && rule.value.map(v => String(v)).join(",");
        } else {
          rawVal = String(rule.value ?? "");
        }

        qs.append("filters[]", `${fieldPart}|${opPart}|${rawVal}`);
      }
      continue;
    }

    qs.set(key, String(value));
  }

  return qs.toString();
};

export function createTableQuerySchema() {
  return z.object({
    sortColumn: z.string().optional(),
    sortDirection: z.enum(["asc", "desc"] as const).optional(),
    search: z.string().optional(),
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).optional(),
    filters: z.array(z.string()).optional(),
  });
}

export const getLeadStrengthColorClass = (leadStrength: number, index: number) => {
  if (leadStrength >= index + 1 && leadStrength >= 2) {
    // Fully filled for this pill threshold: green.
    return "bg-bg-fill-success";
  } else if (leadStrength > index) {
    // Partially filled: orange.
    return "bg-bg-fill-warning";
  }
  return "bg-bg-surface-secondary"; // default gray
};

export const buildInvitationLink = (token: string) => {
  const baseUrl = getBaseUrl();
  return `${baseUrl}/invitation?token=${token}`;
};

export const getFullName = (
  entity: { firstName?: string | null; lastName?: string | null } | null | undefined
) => {
  if (!entity) return "";
  return [entity.firstName, entity.lastName]
    .filter(Boolean)
    .map(s => capitalize(s ?? ""))
    .join(" ");
};

export type WithConnectionStrength = { connectionStrength: number; confidenceScore: number };

export const getStrongestConnection = <T extends WithConnectionStrength>(
  connections: T[]
): T | undefined => {
  return connections.at(0);
};

export const getReconnectCadenceName = (cadance: ReconnectCadence) => {
  switch (cadance) {
    case ReconnectCadence.Weekly:
      return "Weekly";
    case ReconnectCadence.BiWeekly:
      return "Bi-Weekly";
    case ReconnectCadence.Monthly:
      return "Monthly";
    case ReconnectCadence.Quarterly:
      return "Quarterly";
    case ReconnectCadence.Yearly:
      return "Yearly";
    case ReconnectCadence.Disabled:
      return "Disabled";
  }
};
export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const capitalizeSentence = (str: string) => {
  return str
    .split(" ")
    .map(s => capitalize(s))
    .join(" ");
};

export function formatNumber(
  number: number,
  locale?: string,
  options?: Intl.NumberFormatOptions
): string {
  const resolvedLocale =
    locale || (typeof navigator !== "undefined" ? navigator.language : "en-US");

  return new Intl.NumberFormat(resolvedLocale, {
    useGrouping: true,
    ...options,
  }).format(number);
}

export function chunkArray<T>(xs: T[], size: number): T[][] {
  const chunks = [];
  for (let i = 0; i < xs.length; i += size) {
    chunks.push(xs.slice(i, i + size));
  }
  return chunks;
}

export const parseDate = (str?: string): Date | undefined => {
  if (!str) return undefined;

  const parts = str.split("-");
  const year = parseInt(parts[0], 10);
  if (isNaN(year) || year < 1000 || year > 9999) return undefined;

  const month = parts[1] ? parseInt(parts[1], 10) - 1 : 0;
  if (isNaN(month) || month < 0 || month > 11) return undefined;

  const day = parts[2] ? parseInt(parts[2], 10) : 1;
  if (isNaN(day) || day < 1 || day > 31) return undefined;

  const date = new Date(year, month, day);
  return isNaN(date.getTime()) ? undefined : date;
};

export const getCompanyLogo = (companyWebsite: string): string => {
  if (!companyWebsite) return "";
  return `https://img.logo.dev/${companyWebsite}?token=${process.env.NEXT_PUBLIC_LOGO_DEV_API_KEY}&size=100&format=png&retina=true`;
};

export const isServer = () => typeof window === "undefined";

export const deriveNameFromEmail = (
  email: string
): { firstName: string; lastName: string; company: string | null; domain: string | null } => {
  const [localPart, domain] = email.split("@");
  const parts = localPart.split(/[._-]/);

  const capitalizedParts = parts.map(
    part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
  );

  const company = domain
    ? domain.split(".")[0].charAt(0).toUpperCase() + domain.split(".")[0].slice(1)
    : null;

  if (capitalizedParts.length > 1) {
    return {
      firstName: capitalizedParts[0],
      lastName: capitalizedParts.slice(1).join(" "),
      company,
      domain,
    };
  }

  return {
    firstName: capitalizedParts[0],
    lastName: "",
    company,
    domain,
  };
};

const getLuminance = (r = 0, g = 0, b = 0): number => 0.299 * r + 0.587 * g + 0.114 * b;

export const getTextColorForBackground = (
  r?: number,
  g?: number,
  b?: number
): "text-primary" | "text-inverse" => {
  if (r == null || g == null || b == null) return "text-primary";
  return getLuminance(r, g, b) > 186 ? "text-primary" : "text-inverse";
};

type RGB = { r: number; g: number; b: number };

export function getLogoColors(
  colors?: RGB,
  fallback: RGB = { r: 50, g: 50, b: 50 } // dark gray fallback
) {
  const main = colors ?? fallback;

  // treat anything below 50,50,50 as too dark → fallback
  const isTooDark = main.r < 50 && main.g < 50 && main.b < 50;
  const base = isTooDark ? fallback : main;

  // darken by 10%, but clamp to [0, 255]
  const darken = (v: number) => Math.max(0, Math.floor(v * 0.9));

  const darker = {
    r: darken(base.r),
    g: darken(base.g),
    b: darken(base.b),
  };

  const background = `rgb(${darker.r}, ${darker.g}, ${darker.b})`;
  const text = getTextColorForBackground(darker.r, darker.g, darker.b);

  return { background, text };
}

export function hexToRgb(hex: string): RGB {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m, r, g, b) => {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!result) {
    return { r: 50, g: 50, b: 50 };
  }

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}
